/**
 * Подсказки новичку (прототип онбординга): палец и пара слов у кнопки, которую нужно нажать, без окон и паузы.
 * Каждая подсказка одноразовая — гаснет, как только игрок сделал действие, и больше не возвращается (флаг в
 * localStorage). На экране — не больше одной: навык важнее прокачки. Пока открыто окно, подсказок нет: они рисуются
 * над окнами и закрывали бы кнопки.
 *
 * - `skill` — первый готовый навык в бою, до первого нажатия любого навыка (не нажал за 10 с — уступает прокачке);
 * - `level` — вкладка Level, когда на уровень героя хватает монет, затем кнопка покупки внутри; до первой покупки
 *   уровня.
 */
import type { HeroId } from "@newfungame/contracts";
import { createStore, type Store } from "../../app/store";

export type HintId = "skill" | "level" | "parcel";
export const HINT_IDS: readonly HintId[] = ["skill", "level"];

export interface HintsState {
  /** Подсказки, которые игрок уже отработал. */
  done: Record<HintId, boolean>;
  /** Подсказка навыка сейчас на экране — подсказка прокачки ждёт. */
  skillShown: boolean;
  /**
   * Игрок не нажал навык за {@link SKILL_HINT_SNOOZE_MS} — подсказка уступает место прокачке до перезахода
   * (готовый навык иначе держал бы её на экране вечно).
   */
  skillSnoozed: boolean;
}

export const SKILL_HINT_SNOOZE_MS = 10_000;

const KEY = (id: HintId) => `nfg:hint:${id}`;

function readDone(id: HintId): boolean {
  try {
    return window.localStorage.getItem(KEY(id)) === "1";
  } catch {
    return false;
  }
}

export function initialHintsState(): HintsState {
  return {
    done: { skill: readDone("skill"), level: readDone("level"), parcel: readDone("parcel") },
    skillShown: false,
    skillSnoozed: false,
  };
}

export const hintsStore: Store<HintsState> = createStore(initialHintsState());

/** Игрок сделал действие подсказки — больше её не показываем. */
export function completeHint(id: HintId, store = hintsStore): void {
  if (store.get().done[id]) return;
  try {
    window.localStorage.setItem(KEY(id), "1");
  } catch {
    // без localStorage подсказка вернётся после перезахода — не страшно
  }
  store.set((s) => ({ ...s, done: { ...s.done, [id]: true } }));
}

export function setSkillHintShown(on: boolean, store = hintsStore): void {
  store.set((s) => (s.skillShown === on ? s : { ...s, skillShown: on }));
}

export function snoozeSkillHint(store = hintsStore): void {
  store.set((s) => (s.skillSnoozed ? s : { ...s, skillSnoozed: true, skillShown: false }));
}

/** Какому навыку показать палец: первый готовый, пока подсказка не отработана. */
export function skillHintTarget(skills: readonly { heroId: HeroId; ready: boolean }[], done: boolean): HeroId | null {
  if (done) return null;
  return skills.find((s) => s.ready)?.heroId ?? null;
}

/** Где палец прокачки: на вкладке Level (закрыта) или на первой доступной покупке в ней (открыта). */
export type LevelHint = { at: "tab" } | { at: "buy"; heroId: HeroId } | null;

export function levelHintTarget(args: {
  done: boolean;
  skillShown: boolean;
  windowOpen: boolean;
  tab: string | null;
  rows: readonly { id: HeroId; buy: { ok: boolean } }[];
}): LevelHint {
  if (args.done || args.skillShown || args.windowOpen) return null;
  const row = args.rows.find((r) => r.buy.ok);
  if (!row) return null;
  if (args.tab === "level") return { at: "buy", heroId: row.id };
  // открыта другая вкладка — не мешаем
  return args.tab === null ? { at: "tab" } : null;
}
